import {
    ECJuridicalType,
    ECLegalForm,
    ECMember,
    ECMemberAccessType,
    ECMemberParticipationType,
    ECMode,
    EntityType,
    PlantIncentiveMode,
    Pod
} from 'smag-commons/cfg/hierarchy';
import {HierarchyEntity} from './hierarchy.service';
import {can} from './permissions';
import {MeasureAggregationDataSource, Permission} from 'smag-commons/dto/api';
import {BillingPeriod, Mode} from '../views/ec-economics/billing-filter/ec-billing-filter.component';
import {ParamMap} from '@angular/router';
import {isDefined} from '../commons/commons';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexMarkers,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexStroke,
    ApexTitleSubtitle,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
    ChartType
} from 'ng-apexcharts';
import {NiceValuePipe} from './shared.pipe';
import {I18NService} from '../i18n/i18n.service';
import {isConsumerEntity, isProducerEntity, isProsumerEntity} from 'smag-commons/fcn';
import {SharedConfig} from './shared.config';
import {ChartLine} from './line-chart/line-chart.component';
import {MsgBoxService} from './msg-box.service';
import {SubjectInfo} from 'smag-commons/dto/api/subject-info';
import {TimeFilterType} from './time-filter/time-filter.component';
import {SmagApiService} from '../api/smag-api.service';
import {
    ALGORITHM_VAI,
    ECEconomicsAdvancedUserSummary,
    ECEconomicsAdvancedUserTimelyData,
    ECEconomicsAggregatedResponse,
    ECEconomicsAggregatedTimelyData,
    ECEconomicsThirdPartyProducerSummary,
    ECO_ERROR_PARAM_KEY,
    ECO_ERROR_SUBJECT_PARTICIPATION_TYPE_KEY,
    EconomicsErrorType, STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco';
import {
    CACV_MODE,
    COSTS,
    EC_PLANTS_REI_REDISTRIBUTION,
    PLANT_TIP_QUOTE,
    REDISTRIBUTION_SETTINGS
} from 'smag-commons/cfg/economics-values';
import {EconomicsError} from 'smag-commons/dto/eco/errors';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {ValueObjectType} from 'smag-commons/cfg/value';
import {
    ALGORITHM_CONDO_OLD,
    ALGORITHM_FANO,
    ECEconomicsThirdPartyProducerTimelyData
} from 'smag-commons/dto/eco/community';
import {FormGroup} from '@angular/forms';
import {Plant} from 'smag-commons/cfg/hierarchy/entities';
import {environment} from '../../environments/environment';
import {AppConfig} from '../app.config';

export const DEBOUNCE_MILLIS = 250;

export const GIGA_CHAD_DIALOG_MAX_WIDTH = 1400;
export const CHAD_DIALOG_MAX_WIDTH = 1200;
export const CHAD_DIALOG_MIN_WIDTH = 900;
export const DEFAULT_DIALOG_MAX_WIDTH = 1000;
export const DEFAULT_DIALOG_MIN_WIDTH = 700;
export const DEFAULT_PAGE_SIZE = 15;
export const DEFAULT_PAGE_SIZES = [15, 30, 50];

export const CACV_STRING = 'C<sub>ACV</sub>';
export const CPR_BT_STRING = 'C<sub>PR</sub> (BT)';
export const CPR_MT_STRING = 'C<sub>PR</sub> (MT)';

export const CAPITAL_GRANT_MIN = 0;
export const CAPITAL_GRANT_MAX = 40;

export const LOWER_INCENTIVE_BOUNDARY = 1639522800000;
export const UPPER_INCENTIVE_BOUNDARY = 1706050800000;

export const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
];

export const MONTHS_CAPITALS = [
    'J',
    'F',
    'M',
    'A',
    'MA',
    'JU',
    'JL',
    'AU',
    'S',
    'O',
    'N',
    'D'
];

export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc'
}

export interface DataSourceInfo {
    title: string;
    iconName: string;
    iconTitle: string;
    mode: ECMode;
}

export interface ChartOptions {
    chart: ApexChart;
    colors: string[];
    dataLabels: ApexDataLabels;
    labels: string[];
    apexLegends: ApexLegend;
    plotOptions: ApexPlotOptions;
    series: ApexNonAxisChartSeries;
    title: ApexTitleSubtitle;
    tooltip: ApexTooltip;
    subtitle: ApexTitleSubtitle;
}

export interface AxisChartOptions {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    stroke: ApexStroke;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    colors: string[];
    fill: ApexFill;
    legend: ApexLegend;
}

export function isViewable(entity: HierarchyEntity): boolean {
    return can(entity.permissions, Permission.READ);
}

export function isUpdatable(entity: HierarchyEntity): boolean {
    return can(entity.permissions, Permission.UPDATE);
}


export function mkBillingPeriodParams(billingPeriod: BillingPeriod): object {
    return !billingPeriod ?
        {} :
        {
            from: billingPeriod.fromTime,
            to: billingPeriod.toTime,
            mode: billingPeriod.mode === Mode.RANGE ? 'range' : 'single',
            type: billingPeriod.type
        };
}

export function loadBillingPeriod(params: ParamMap): BillingPeriod {
    const fromTime = params.get('from') ? Number.parseInt(params.get('from'), 10) : null;
    const toTime = params.get('to') ? Number.parseInt(params.get('to'), 10) : null;
    const mode = params.get('mode') && params.get('mode') === 'range' ? Mode.RANGE : Mode.SINGLE;
    const type = params.get('type') ? params.get('type') as TimeFilterType : null;
    if (isDefined(fromTime) && isDefined(toTime) && isDefined(mode)) {
        return {
            fromTime,
            toTime,
            mode,
            type
        };
    }
    return null;
}

export function isCurrentMonth(date: Moment, timeZone: string): boolean {
    const now = moment.tz(timeZone);
    return date.month() === now.month() && date.year() === now.year();
}

export function isCurrentDay(date: Moment, timeZone: string): boolean {
    const now = moment.tz(timeZone);
    // .day()  -> day of week
    // .date() -> day of month
    return date.date() === now.date() && date.month() === now.month() && date.year() === now.year();
}

export function isFutureDate(month: number, year: number, now: Moment): boolean {
    return year > now.year() ||
        month > now.month() && year === now.year();
}

export function isSingleMonthSelected(billingPeriod: BillingPeriod, timeZone: string, doShowNullData: boolean) {
    const from = billingPeriod.fromTime;
    const to = billingPeriod.toTime;
    const nextMont = moment.tz(from, timeZone).add(1, 'month').startOf('month');
    return to === nextMont.valueOf() || billingPeriod.type === TimeFilterType.SINGLE_MONTH || billingPeriod.type === TimeFilterType.THIS_MONTH || doShowNullData;
}

export function getDataSourceIconInfo(ecMode: ECMode): DataSourceInfo {
    if (ecMode === ECMode.BASE) {
        return {
            title: 'Estimated data from statistical profiles',
            iconName: 'fal-chart-line',
            iconTitle: 'Energy data are estimated on GSE-defined statistical profiles and previous year\'s readings, in similar seasonality, and based on rate ranges',
            mode: ecMode
        };
    } else if (ecMode === ECMode.ADVANCED) {
        return {
            title: 'Estimated from monitoring',
            iconName: 'fal-radar',
            iconTitle: 'Computations based on data derived from smart meters.',
            mode: ecMode
        };
    } else if (ecMode === ECMode.HYBRID) {
        return {
            title: 'Estimated from hybrid data',
            iconName: 'fal-shapes',
            iconTitle: 'Computations based on data from smart meters and statistical profiles.',
            mode: ecMode
        };
    }
}

const MAX_FILE_MB = 2;
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export function validateFileSize(file: File, i18n: I18NService, msgBoxService: MsgBoxService): boolean {
    if (file.size > MAX_FILE_MB * 1048576) {
        msgBoxService.error(i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
        return false;
    }
    if (file.type !== XLSX_MIME_TYPE) {
        msgBoxService.error(i18n.translate('File {0} is not an XLSX file', file.name));
        return false;
    }
    return true;
}

export function getPodType(hierarchyEntity: HierarchyEntity, pod: Pod) {
    if (isProsumerEntity(hierarchyEntity, pod)) {
        return 'Prosumer';
    } else if (isConsumerEntity(hierarchyEntity, pod)) {
        return 'Consumer';
    } else if (isProducerEntity(hierarchyEntity, pod)) {
        return 'Producer';
    }
    return null;
}


// chart functions
export function getTranslatedLabels(labels: string[], i18n: I18NService) {
    return labels.map(l => i18n.translate(l));
}

export function getNiceTooltip(nvp: NiceValuePipe) {
    return {
        y: {
            formatter: (value: number, opts) =>
                nvp.transform(Math.abs(value) * 100 / opts.globals.series.reduce((acc: number, num: number) => acc + num, 0), '%', 2, false)
        }
    };
}

export function disableDataLabels(): ApexDataLabels {
    return {enabled: false};
}

export function disableLegend(): ApexLegend {
    return {show: false};
}

export function getDonutWithSize(size?: number): ApexPlotOptions {
    const defaultSize = 65;
    return {
        pie: {
            donut: {
                size: `${(isDefined(size) ? size : defaultSize)}%`
            }
        }
    };
}

export function getPieWithOffsetX(offsetX: number): ApexPlotOptions {
    return {pie: {offsetX}};
}

export function getDonutWithSizeAndOffsetX(donutSize: number, offsetX: number): ApexPlotOptions {
    return {
        pie: {
            donut: {
                size: `${donutSize}%`
            },
            offsetX
        }
    };
}

export function getChartWithOffsetYAndHeight(chart: string, offsetY?: number, height?: number): ApexChart {
    const defaultOffsetY = -40;
    const defaultHeight = 200;
    return {
        type: chart as ChartType,
        offsetY: isDefined(offsetY) ? offsetY : defaultOffsetY,
        height: isDefined(height) ? height : defaultHeight
    };
}


// end of chart functions

export function getSubjectName(subject: ECMember) {
    return (subject.juridical_type === ECJuridicalType.INDIVIDUAL && subject.legal_form === ECLegalForm.OTHER_INDIVIDUALS) ?
        subject.personal_data.first_name + ' ' + subject.personal_data.last_name :
        subject.personal_data.company_name;
}


export function getEffectiveInjectedColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.effectiveInjectedEnergy;
}

export function getAbsorbedPowerColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.absorbedPower;
}

export function getSharedPowerColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.sharedPower;
}

export function getConsolidatedEffectiveInjectedColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.consolidatedEffectiveInjected;
}

export function getConsolidatedAbsorbedPowerColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.consolidatedAbsorbed;
}

export function getConsolidatedSharedPowerColor(sharedConfig: SharedConfig): string {
    return sharedConfig.colors.consolidatedShared;

}

export function getGridColor(sharedConfig: SharedConfig): string {
    return sharedConfig.gridColor;
}

export function getTooltipBorderColor(sharedConfig: SharedConfig): string {
    return sharedConfig.tooltipBorderColor;
}

export function getTooltipBackgroundColor(sharedConfig: SharedConfig): string {
    return sharedConfig.tooltipBackgroundColor;
}

export function getSelectionLineColor(sharedConfig: SharedConfig): string {
    return sharedConfig.selectionLineColor;
}

export function noData(data: ChartLine[]): boolean {
    return data.every(chartLine => chartLine.data.every(([t, d]) => !isDefined(d)));
}


export enum ControlPanelUserType {
    SUPERVISOR = 'supervisor',
    ADVANCED = 'advanced',
    THIRD_PARTY_PRODUCER = 'third_party_producer'
}

export function getControlPanelUserType(subject: SubjectInfo): ControlPanelUserType {
    if (!subject || subject.subject.access_type === ECMemberAccessType.NONE) {
        return ControlPanelUserType.SUPERVISOR;
    } else if (subject.subject.access_type === ECMemberAccessType.ADVANCED &&
        subject.subject.participation_type === ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
        return ControlPanelUserType.THIRD_PARTY_PRODUCER;
    } else if (subject.subject.access_type === ECMemberAccessType.ADVANCED &&
        subject.subject.participation_type === ECMemberParticipationType.MEMBER) {
        return ControlPanelUserType.ADVANCED;
    }
    return null;
}


export function sanityToTime(timeZone: string, time: number): number {
    const now = moment.tz(timeZone).startOf('hour').valueOf();
    return Math.min(now, time);
}

/**
 * Given a "fromTime" (1st temporal range of an interval) and the activation date of the community, it returns the maximum of the two.
 * This function is intended to act as a limiter for t1 in the time interval [t1, t2] utilized to compute the community's economic values.
 * The time we return cannot be higher than the current day (avoid futures dates)
 */
export function sanityFromTimeActivationDate(fromTime: number, activationDate: number, timeZone: string): number {
    if (activationDate) {
        return Math.min(Math.max(fromTime, activationDate), moment.tz(timeZone).startOf('day').valueOf());
    }
    return fromTime;
}


export function isEnergyCommunity(he: HierarchyEntity) {
    return he?.type === EntityType.ENERGY_COMMUNITY;
}


export interface YearSummaryConsolidation {
    year: number;
    month: number;
    consolidated: boolean;
}

export async function mkConsolidatedMonths(id: string, pastDate: number, timeZone: string, apiService: SmagApiService) {
    const ecActivationDate = moment.tz(pastDate, timeZone);
    const now = moment.tz(timeZone);
    const summaries: YearSummaryConsolidation[] = [];
    const date = {};
    // ignore the days of the months to avoid incorrect results caused by partial months
    const monthDifference = now.clone().startOf('month').diff(ecActivationDate.clone().startOf('month'), 'months');

    const years = new Set<number>();
    for (let i = 0; i < monthDifference + 1; i++) {
        const month = ecActivationDate.clone().add(i, 'month').month();
        const year = ecActivationDate.clone().add(i, 'month').year();
        years.add(year);
        if (date[year]) {
            date[year].push(month);
        } else {
            date[year] = [month];
        }
    }

    try {
        for (const year of years) {
            const consolidated = await apiService.getConsolidationByYear(id, year).toPromise();
            const months = date[year];
            months.forEach((month: number) => {
                // back-end months span from 1 to 12
                const consolidatedMonth = consolidated[month + 1];
                summaries.push({month, year, consolidated: isDefined(consolidatedMonth) ? consolidatedMonth : false});
            });
        }
        return summaries;
    } catch (err) {
        console.error(err);
        return [] as YearSummaryConsolidation[];
    }
}

export function getPastMonth(timeZone: string) {
    return moment.tz(timeZone).subtract(1, 'month').endOf('month');
}

export function getMomentFromMs(ms: number, timeZone: string) {
    return moment.tz(ms, timeZone);
}

export function isWholeYearSelected(startDate: number, endDate: number, timeZone: string, type?: TimeFilterType) {
    if (!!type && (type === TimeFilterType.THIS_YEAR || type === TimeFilterType.LAST_YEAR)) {
        return true;
    } else {
        const startMoment = moment.tz(startDate, timeZone);
        const endMoment = moment.tz(endDate, timeZone);
        const isStartJanuary = startMoment.valueOf() === startMoment.startOf('year').valueOf();
        const isStartJanuaryNextYear = endMoment.valueOf() === startMoment.add(1, 'year').startOf('year').valueOf();
        return isStartJanuary && isStartJanuaryNextYear;
    }
}

export function isWholeMonthSelected(startDate: number, endDate: number, timeZone: string, type?: TimeFilterType) {
    if (!!type && (type === TimeFilterType.SINGLE_MONTH || type === TimeFilterType.LAST_MONTH)) {
        return true;
    } else {
        const startMoment = moment.tz(startDate, timeZone);
        const endMoment = moment.tz(endDate, timeZone);
        const isStartMonth = startMoment.valueOf() === startMoment.startOf('month').valueOf();
        const isStartNextMonth = endMoment.valueOf() === startMoment.add(1, 'month').startOf('month').valueOf();
        return isStartMonth && isStartNextMonth;
    }
}

export function getTimelyData(response: any) {
    if (response.hourly_data) {
        return response.hourly_data;
    } else if (response.daily_data) {
        return response.daily_data;
    } else if (response.weekly_data) {
        return response.weekly_data;
    } else if (response.monthly_data) {
        return response.monthly_data;
    }
}

export function consolidationsAvailable(response: ECEconomicsAdvancedUserSummary | ECEconomicsAggregatedResponse | ECEconomicsThirdPartyProducerSummary) {
    if (!isDefined(response)) {
        return false;
    }
    const timelyData = getTimelyData(response);
    const absGSE = filterDataSource(timelyData.absorbed_energy_data_source);
    const injGSE = filterDataSource(timelyData.injected_energy_data_source);
    const prGSE = filterDataSource(timelyData.produced_energy_data_source);
    return absGSE || injGSE || prGSE;
}

function filterDataSource(measureAggregationDataSources: MeasureAggregationDataSource[]) {
    return measureAggregationDataSources.filter(dataSource => dataSource === MeasureAggregationDataSource.GSE).length > 0;
}

export function allGSE(timelyData: ECEconomicsThirdPartyProducerTimelyData | ECEconomicsAggregatedTimelyData | ECEconomicsAdvancedUserTimelyData,
                       index: number): boolean {
    return timelyData.injected_energy_data_source[index] === MeasureAggregationDataSource.GSE &&
        timelyData.absorbed_energy_data_source[index] === MeasureAggregationDataSource.GSE;
}


/******************************************************************************************************/

// EconomicsError

// generic
function missingParam(response: any, param: string, strategy: string) {
    if (strategy === STRATEGY_OLD_CONDO) {
        return false;
    }
    return response?.errors.some((error: EconomicsError) =>
        error.type === EconomicsErrorType.MISSING_PARAM &&
        error.metadata[ECO_ERROR_PARAM_KEY] === param);
}

function missingParamWithType(response: any, param: string, participationType: ECMemberParticipationType, strategy: string) {
    if (strategy === STRATEGY_OLD_CONDO) {
        return false;
    }
    return response?.errors.some((error: EconomicsError) =>
        error.type === EconomicsErrorType.MISSING_PARAM &&
        error.metadata[ECO_ERROR_PARAM_KEY] === param &&
        error.metadata[ECO_ERROR_SUBJECT_PARTICIPATION_TYPE_KEY] === participationType);
}

// CACV
export function missingCacvMode(response: any, strategy: string) {
    return missingParam(response, CACV_MODE, strategy);
}

// TIP
export function missingTipQuote(response: any, strategy: string) {
    return missingParam(response, PLANT_TIP_QUOTE, strategy);
}

export function missingTipQuoteWithType(response: any, participationType: ECMemberParticipationType, strategy: string) {
    return missingParamWithType(response, PLANT_TIP_QUOTE, participationType, strategy);
}

// costs
export function missingCosts(response: any, strategy: string) {
    return missingParam(response, COSTS, strategy);
}

// REI
export function missingReiRedistribution(response: any, strategy: string) {
    return missingParam(response, EC_PLANTS_REI_REDISTRIBUTION, strategy);
}

// redistribution settings
export function missingRedistributionSettings(response: any) {
    return missingParam(response, REDISTRIBUTION_SETTINGS, null);
}

export function isCommunityConfigured(response: any, strategy: string) {

    if (strategy === STRATEGY_OLD_CONDO) {
        return false;
    }

    return (response.errors || [])
        .filter((error: EconomicsError) => error.type === EconomicsErrorType.MISSING_PARAM)
        .some((error: EconomicsError) =>
            error.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
            error.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
            error.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
}

/******************************************************************************************************/

// other stuff

export function mkYearlyValue(name: string, value: any, id: string, year: number): YearlyValue {
    return {
        name,
        object_id: id,
        object_type: ValueObjectType.RESOURCE,
        year,
        value
    };
}


export function generatesTip(plant: Plant): boolean {
    return plant.incentive_mode === PlantIncentiveMode.OLD_REGULATIONS || plant.incentive_mode === PlantIncentiveMode.NEW_REGULATIONS;
}


export function limitDecimals(costForm: FormGroup, name: string) {
    // limit decimals to 2 digits
    const control = costForm.controls[name];
    const value: number = control.value;
    if (!value || (value.toString().split('.')[1] ?? '').length <= 2) {
        return;
    }
    control.setValue(Math.trunc(value * 100) / 100);
}

export function getAlgoDialogWidth(algorithm: string): number {
    if (algorithm === ALGORITHM_VAI || algorithm === ALGORITHM_FANO) {
        return 22;
    }
    if (algorithm === ALGORITHM_CONDO_OLD) {
        return 30;
    }
    return 17;
}

export function showNullData(inactiveEc: boolean, futureEc: boolean): boolean {
    return inactiveEc || futureEc;
}

export function keepYourSecrets(inactiveEc: boolean, futureEc: boolean, communityNotConfigured: boolean): boolean {
    return showNullData(inactiveEc, futureEc) || communityNotConfigured;
}

export function mkPersonalSharingChart(nvp: NiceValuePipe, i18n: I18NService, personalSharing: number): Partial<ChartOptions> {
    return {
        chart: {type: 'donut', height: 80},
        colors: [environment.appColors.ecoYellow, environment.appColors.ecoEmpty],
        dataLabels: disableDataLabels(),
        apexLegends: disableLegend(),
        plotOptions: {
            pie: {
                startAngle: -90,
                endAngle: 90,
                donut: {
                    size: '65%'
                }
            }
        },
        labels: getTranslatedLabels(['Personal sharing', ''], i18n),
        series: personalSharing ? [personalSharing, 100 - personalSharing] : [0, 0],
        tooltip: getNiceTooltip(nvp),
        subtitle: {
            text: nvp.transform(personalSharing ? personalSharing : 0, '%', 2),
            align: 'center',
            offsetY: 30,
            floating: true,
            style: {
                fontSize: '12px',
                fontWeight: '400',
                fontFamily: environment.fontFamily,
                color: environment.appColors.ecoDarkLegend
            }
        }
    };
}

export function mkAbsorptionParticipationChart(nvp: NiceValuePipe, i18n: I18NService, absorptionParticipation: number, title?: string): Partial<ChartOptions> {
    return {
        chart: {type: 'donut', height: 80},
        colors: [environment.appColors.ecoYellow, environment.appColors.ecoEmpty],
        dataLabels: disableDataLabels(),
        apexLegends: disableLegend(),
        plotOptions: {
            pie: {
                donut: {
                    size: '65%'
                }
            }
        },
        labels: getTranslatedLabels([title ?? 'Participation in community sharing', ''], i18n),
        series: absorptionParticipation ? [absorptionParticipation, 100 - absorptionParticipation] : [0, 0],
        tooltip: getNiceTooltip(nvp)
    };
}


export function mkSharedChart(cfg: AppConfig,
                              i18n: I18NService,
                              response: ECEconomicsAdvancedUserSummary | ECEconomicsThirdPartyProducerSummary | ECEconomicsAggregatedResponse,
                              showIncentivized: boolean,
                              sharedEnergyEstimatedData: boolean,
                              sharedEnergyConsolidatedData: boolean,
                              isThirdPartyProducer = false,
                              useMWh = false) {
    const timelyData = getTimelyData(response);
    const {series, colors} = extractEnergiesSeriesAndColors(cfg, i18n, timelyData, showIncentivized,
        sharedEnergyEstimatedData, sharedEnergyConsolidatedData, isThirdPartyProducer);
    const it = require('apexcharts/dist/locales/it.json');
    const en = require('apexcharts/dist/locales/en.json');
    return {
        series,
        chart: {
            type: 'line',
            height: 350,
            stacked: true,
            stackType: 'normal',
            stackOnlyBar: true,
            locales: [it, en],
            defaultLocale: 'it',
            toolbar: {
                tools: {
                    download: false,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true,
                }
            }
        } as ApexChart,
        stroke: {
            show: true,
            curve: 'smooth',
            width: 1,
        } as ApexStroke,
        dataLabels: {
            enabled: false
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '100%',
                distributed: false,
            }
        },
        tooltip: {
            enabled: true,
            shared: true,
            followCursor: true,
            hideEmptySeries: true
        },
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
            }
        } as ApexXAxis,
        fill: {
            opacity: 1,
            type: 'linear'
        },
        colors,
        yaxis: {
            labels: {
                formatter(y: number) {
                    return getFormatter(y, useMWh);
                }
            }
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center'
        } as ApexLegend
    };
}

function extractEnergiesSeriesAndColors(cfg: AppConfig,
                                        i18n: I18NService,
                                        timelyData: ECEconomicsAdvancedUserTimelyData |
                                            ECEconomicsAggregatedTimelyData |
                                            ECEconomicsThirdPartyProducerTimelyData,
                                        showIncentivized: boolean,
                                        sharedEnergyEstimatedData: boolean,
                                        sharedEnergyConsolidatedData: boolean,
                                        isThirdPartyProducer: boolean) {
    const {
        absorbedEnergy,
        injectedEnergy,
        sharedEnergy,
        consolidatedSharedEnergy,
        consolidatedAbsorbedEnergy,
        consolidatedInjectedEnergy,
        incentivizedInjectedEnergy,
        incentivizedConsolidatedInjectedEnergy,
        incentivizedConsolidatedSharedEnergy,
        incentivizedSharedEnergy
    } = extractEnergies(timelyData, isThirdPartyProducer);
    const series = [
        sharedEnergyEstimatedData ? {
            name: i18n.translate('Total absorbed energy (estimated)'),
            group: 'estimated',
            data: absorbedEnergy,
            type: 'column'
        } : null,
        sharedEnergyConsolidatedData ? {
            name: i18n.translate('Total absorbed energy (billed)'),
            group: 'billed',
            data: consolidatedAbsorbedEnergy,
            type: 'column'
        } : null,
        sharedEnergyEstimatedData ? {
            name: i18n.translate('Total effective injected energy (estimated)'),
            group: 'estimated',
            data: injectedEnergy,
            type: 'column'
        } : null,
        sharedEnergyEstimatedData && showIncentivized ? {
            name: i18n.translate('Incentivized Effective injected energy (estimated)'),
            group: 'estimated',
            data: incentivizedInjectedEnergy,
            type: 'column'
        } : null,
        sharedEnergyConsolidatedData ? {
            name: i18n.translate('Total effective injected energy (billed)'),
            data: consolidatedInjectedEnergy,
            group: 'billed',
            type: 'column'
        } : null,
        sharedEnergyConsolidatedData && showIncentivized ? {
            name: i18n.translate('Incentivized Effective injected energy (billed)'),
            group: 'billed',
            data: incentivizedConsolidatedInjectedEnergy,
            type: 'column'
        } : null,
        sharedEnergyEstimatedData && showIncentivized ? {
            name: i18n.translate('Incentivized shared energy (estimated)'),
            data: incentivizedSharedEnergy,
            type: 'area'
        } : null,
        sharedEnergyEstimatedData ? {
            name: i18n.translate('Total shared energy (estimated)'),
            data: sharedEnergy,
            type: 'area'
        } : null,
        sharedEnergyConsolidatedData && showIncentivized ? {
            name: i18n.translate('Incentivized shared energy (billed)'),
            data: incentivizedConsolidatedSharedEnergy,
            type: 'line'
        } : null,
        sharedEnergyConsolidatedData ? {
            name: i18n.translate('Total shared energy (billed)'),
            data: consolidatedSharedEnergy,
            type: 'line'
        } : null
    ];
    const colors = [sharedEnergyEstimatedData ? cfg.shared.colors.absorbedPower : null,
        sharedEnergyConsolidatedData ? cfg.shared.colors.consolidatedAbsorbed : null,
        sharedEnergyEstimatedData ? cfg.shared.colors.effectiveInjectedEnergy : null,
        sharedEnergyEstimatedData && showIncentivized ? cfg.shared.colors.incentivizedEffectiveInjected : null,
        sharedEnergyConsolidatedData ? cfg.shared.colors.consolidatedEffectiveInjected : null,
        sharedEnergyConsolidatedData && showIncentivized ? cfg.shared.colors.incentivizedConsolidatedEffectiveInjected : null,
        sharedEnergyEstimatedData && showIncentivized ? cfg.shared.colors.incentivizedShared : null,
        sharedEnergyEstimatedData ? cfg.shared.colors.sharedPower : null,
        sharedEnergyConsolidatedData && showIncentivized ? cfg.shared.colors.incentivizedConsolidatedShared : null,
        sharedEnergyConsolidatedData ? cfg.shared.colors.consolidatedShared : null];
    return {series: series.filter(s => isDefined(s)), colors: colors.filter(c => isDefined(c))};
}

function extractEnergies(timelyData: ECEconomicsAdvancedUserTimelyData |
                             ECEconomicsAggregatedTimelyData |
                             ECEconomicsThirdPartyProducerTimelyData,
                         isThirdPartyProducer: boolean) {
    const absorbedEnergy = [];
    const injectedEnergy = [];
    const sharedEnergy = [];
    const consolidatedSharedEnergy = [];
    const consolidatedAbsorbedEnergy = [];
    const consolidatedInjectedEnergy = [];
    const incentivizedInjectedEnergy = [];
    const incentivizedConsolidatedInjectedEnergy = [];
    const incentivizedConsolidatedSharedEnergy = [];
    const incentivizedSharedEnergy = [];
    for (let i = 0; i < timelyData.times.length; i++) {
        absorbedEnergy.push({
            x: timelyData.times[i],
            y: isThirdPartyProducer ?
                -(timelyData as ECEconomicsThirdPartyProducerTimelyData).community_estimated_absorbed_energy[i] :
                -(timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).estimated_absorbed_energy[i]
        });
        injectedEnergy.push({x: timelyData.times[i], y: timelyData.estimated_effective_injected_energy[i]});
        consolidatedAbsorbedEnergy.push({
            x: timelyData.times[i],
            y: timelyData.absorbed_energy_data_source[i] === MeasureAggregationDataSource.GSE ?
                (isThirdPartyProducer ?
                    -(timelyData as ECEconomicsThirdPartyProducerTimelyData).community_absorbed_energy[i] :
                    -(timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).absorbed_energy[i]) :
                null
        });
        consolidatedInjectedEnergy.push({
            x: timelyData.times[i],
            y: timelyData.injected_energy_data_source[i] === MeasureAggregationDataSource.GSE ? timelyData.effective_injected_energy[i] : null
        });
        incentivizedInjectedEnergy.push({
            x: timelyData.times[i],
            y: isThirdPartyProducer ?
                (timelyData as ECEconomicsThirdPartyProducerTimelyData).estimated_incentive_effective_injected_energy[i] :
                (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).estimated_incentivized_effective_injected_energy[i]
        });
        incentivizedConsolidatedInjectedEnergy.push({
            x: timelyData.times[i],
            y: timelyData.injected_energy_data_source[i] === MeasureAggregationDataSource.GSE ?
                (isThirdPartyProducer ?
                    (timelyData as ECEconomicsThirdPartyProducerTimelyData).incentive_effective_injected_energy[i] :
                    (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).incentivized_effective_injected_energy[i]) :
                null
        });
        sharedEnergy.push({x: timelyData.times[i], y: timelyData.estimated_shared_energy[i]});
        consolidatedSharedEnergy.push({
            x: timelyData.times[i],
            y: allGSE(timelyData, i) ? timelyData.shared_energy[i] : null
        });
        incentivizedSharedEnergy.push({
            x: timelyData.times[i],
            y: isThirdPartyProducer ?
                (timelyData as ECEconomicsThirdPartyProducerTimelyData).incentive_estimated_shared_energy[i] :
                (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).estimated_incentivized_shared_energy[i]
        });
        incentivizedConsolidatedSharedEnergy.push({
            x: timelyData.times[i],
            y: allGSE(timelyData, i) ?
                (isThirdPartyProducer ? (timelyData as ECEconomicsThirdPartyProducerTimelyData).incentive_shared_energy[i] :
                    (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).incentivized_shared_energy[i]) :
                null
        });
    }
    return {
        absorbedEnergy,
        injectedEnergy,
        sharedEnergy,
        consolidatedSharedEnergy,
        consolidatedAbsorbedEnergy,
        consolidatedInjectedEnergy,
        incentivizedInjectedEnergy,
        incentivizedConsolidatedInjectedEnergy,
        incentivizedConsolidatedSharedEnergy,
        incentivizedSharedEnergy
    };
}


export function mkEnergyRatioChart(cfg: AppConfig,
                                   i18n: I18NService,
                                   response: ECEconomicsAdvancedUserSummary | ECEconomicsThirdPartyProducerSummary | ECEconomicsAggregatedResponse,
                                   showIncentivized: boolean,
                                   energyRatioEstimatedData: boolean,
                                   energyRatioConsolidatedData: boolean,
                                   isThirdPartyProducer = false) {
    const timelyData = getTimelyData(response);
    const {series, colors} = extractEnergyRatioSeriesAndColors(cfg, i18n, timelyData, showIncentivized,
        energyRatioEstimatedData, energyRatioConsolidatedData, isThirdPartyProducer);
    const it = require('apexcharts/dist/locales/it.json');
    const en = require('apexcharts/dist/locales/en.json');

    return {
        series,
        chart: {
            type: 'line',
            height: 350,
            stacked: false,
            locales: [it, en],
            defaultLocale: 'it',
            toolbar: {
                tools: {
                    download: false,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true,
                }
            }
        } as ApexChart,
        stroke: {
            show: true,
            curve: 'smooth',
            width: 2,
        } as ApexStroke,
        markers: {
            size: 6,
            colors,
        } as ApexMarkers,
        dataLabels: {
            enabled: false
        },
        tooltip: {
            enabled: true,
            shared: true,
            followCursor: false,
            hideEmptySeries: true,
        },
        plotOptions: {},
        xaxis: {
            type: 'datetime',
            labels: {
                datetimeUTC: false,
            }
        } as ApexXAxis,
        fill: {},
        colors,
        yaxis: {
            labels: {
                formatter(y: number) {
                    return isDefined(y) ? (y * 100).toFixed(2) + '%' : null;
                }
            }
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center'
        } as ApexLegend
    };
}

function extractEnergyRatioSeriesAndColors(cfg: AppConfig,
                                           i18n: I18NService,
                                           timelyData: ECEconomicsAdvancedUserTimelyData |
                                               ECEconomicsAggregatedTimelyData |
                                               ECEconomicsThirdPartyProducerTimelyData,
                                           showIncentivized: boolean,
                                           energyRatioEstimatedData: boolean,
                                           energyRatioConsolidatedData: boolean,
                                           isThirdPartyProducer: boolean) {
    const {
        energyRatio,
        incentivizedEnergyRatio,
        consolidatedEnergyRatio,
        incentivizedConsolidatedEnergyRatio,
    } = extractRatios(timelyData, isThirdPartyProducer);
    const series = [
        energyRatioEstimatedData ? {
            name: i18n.translate('Sharing Performance (estimated)'),
            data: energyRatio,
        } : null,
        energyRatioEstimatedData && showIncentivized ? {
            name: i18n.translate('Incentivized sharing performance (estimated)'),
            data: incentivizedEnergyRatio,
        } : null,
        energyRatioConsolidatedData ? {
            name: i18n.translate('Sharing Performance (billed)'),
            data: consolidatedEnergyRatio,
        } : null,
        energyRatioConsolidatedData && showIncentivized ? {
            name: i18n.translate('Incentivized sharing performance (billed)'),
            data: incentivizedConsolidatedEnergyRatio,
        } : null
    ];
    const colors = [
        energyRatioEstimatedData ? cfg.shared.colors.sharedEnergyRatio : null,
        energyRatioEstimatedData && showIncentivized ? cfg.shared.colors.incentiveSharedEnergyRatio : null,
        energyRatioConsolidatedData ? cfg.shared.colors.consolidatedSharedRatio : null,
        energyRatioConsolidatedData && showIncentivized ? cfg.shared.colors.consolidatedIncentiveSharedRatio : null,
    ];
    return {series: series.filter(s => isDefined(s)), colors: colors.filter(c => isDefined(c))};
}

function extractRatios(timelyData: ECEconomicsAdvancedUserTimelyData |
                           ECEconomicsAggregatedTimelyData |
                           ECEconomicsThirdPartyProducerTimelyData,
                       isThirdPartyProducer: boolean) {
    const energyRatio = [];
    const incentivizedEnergyRatio = [];
    const consolidatedEnergyRatio = [];
    const incentivizedConsolidatedEnergyRatio = [];

    for (let i = 0; i < timelyData.times.length; i++) {
        energyRatio.push({x: timelyData.times[i], y: timelyData.estimated_shared_energy_ratio[i]});
        incentivizedEnergyRatio.push({
            x: timelyData.times[i],
            y: isThirdPartyProducer ?
                (timelyData as ECEconomicsThirdPartyProducerTimelyData).estimated_incentive_shared_energy_ratio[i] :
                (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).estimated_incentivized_shared_energy_ratio[i]
        });
        consolidatedEnergyRatio.push({
            x: timelyData.times[i],
            y: allGSE(timelyData, i) ? timelyData.shared_energy_ratio[i] : null
        });
        incentivizedConsolidatedEnergyRatio.push({
            x: timelyData.times[i],
            y: allGSE(timelyData, i) ?
                (isThirdPartyProducer ?
                    (timelyData as ECEconomicsThirdPartyProducerTimelyData).incentive_shared_energy_ratio[i] :
                    (timelyData as ECEconomicsAdvancedUserTimelyData | ECEconomicsAggregatedTimelyData).incentivized_shared_energy_ratio[i]) :
                null
        });
    }
    return {
        energyRatio,
        incentivizedEnergyRatio,
        consolidatedEnergyRatio,
        incentivizedConsolidatedEnergyRatio,
    };
}

function getFormatter(y: number, useMWh: boolean) {
    if (!isDefined(y)) {
        return y ? y.toString() : null;
    }

    if (useMWh) {
        return Math.abs(y) >= 1000 ? (y / 1000).toFixed(2) + ' MWh' : y.toFixed(2) + ' KWh';
    }

    if (!useMWh) {
        return (y / 1000).toFixed(2) + ' KWh';
    }

}

